import { FIELD_TYPES } from '../../../../../constants';
import {
	hasTranslationContent,
	hasTranslationDeleted,
} from '../../../utils/translations';
import {
	FORM_EDITOR_FIELDS,
	FIELD_EDITOR_FIELDS,
	OPTION_EDITOR_FIELDS,
	SECTION_EDITOR_FIELDS,
} from '../constants';

import {
	formFieldOptionDataReducers,
	FORM_FIELD_OPTION_DATA_DISPATCH,
} from './formFieldOptions';
import {
	formFieldDataReducers,
	FORM_FIELD_DATA_DISPATCH,
} from './formFields';
import {
	formSectionDataReducers,
	FORM_SECTION_DATA_DISPATCH,
} from './formSections';

export const FORM_EDITOR_DATA_DISPATCH = {
	ADD_TRANSLATION: 'ADD_TRANSLATION',
	DELETE_TRANSLATION: 'DELETE_TRANSLATION',
	UPDATE_FORM_LANGUAGE: 'UPDATE_FORM_LANGUAGE',
	...FORM_FIELD_DATA_DISPATCH,
	...FORM_FIELD_OPTION_DATA_DISPATCH,
	...FORM_SECTION_DATA_DISPATCH,
};

/**
 * Set of functions that update the form state for a given action type.
 */
export const formEditorDataReducers = {
	[FORM_EDITOR_DATA_DISPATCH.ADD_TRANSLATION]: addTranslation,
	[FORM_EDITOR_DATA_DISPATCH.DELETE_TRANSLATION]: deleteTranslation,
	[FORM_EDITOR_DATA_DISPATCH.UPDATE_FORM_LANGUAGE]: updateLanguage,
	...formFieldDataReducers,
	...formFieldOptionDataReducers,
	...formSectionDataReducers,
};

/**
 * Updates the state to the selected language and loads the multi-language content for the
 * newly selected language if the form data is not already loaded.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action                     Data related to the requested action.
 * @param {String} action.fieldValue          The new value of the field to update.
 * @param {Function} action.onLanguageChange  Callback to fetch the requested language.
 *
 * @returns {Object} result  The updated form data state.
 */
async function updateLanguage(formData, action) {

	const { fieldValue, onLanguageChange } = action;

	const newLanguage = fieldValue;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Check to see if a translation exists for the new language
	const translationExists = hasTranslationContent(result, newLanguage);

	// Check to see if translation has been set to be deleted for the new language
	const translationDeleted = hasTranslationDeleted(result, newLanguage);

	// Load the translation (if any) if not already loaded for the new language

	if (result.id && !translationExists && !translationDeleted) {

		const { data: loadedForm } = await onLanguageChange(result.id, newLanguage);

		if (!loadedForm) {
			throw new Error('Loaded form undefined');
		}

		// Loop through each section in the form and add the new language content

		result[FORM_EDITOR_FIELDS.SECTIONS].forEach((rSection) => {

			// Find the matching section in the loaded form data
			const loadedSection = loadedForm.form_sections?.find(
				(temp) => temp.id === rSection.id
			);

			// Find the translated content for the section in the new language, if any
			const sectionTranslation = loadedSection?.form_section_contents.find(
				(sContent) => sContent.language === newLanguage
			);

			// If a translation exists, add it to the form state

			if (sectionTranslation) {

				rSection[SECTION_EDITOR_FIELDS.CONTENTS].push({
					language: newLanguage,
					[SECTION_EDITOR_FIELDS.TITLE]: sectionTranslation.title,
					[SECTION_EDITOR_FIELDS.DESCRIPTION]: sectionTranslation.description,
				});

			}

			// Loop through each field in the section and add the new language content

			rSection[SECTION_EDITOR_FIELDS.FIELDS].forEach((rField) => {

				// Find the matching field in the loaded form data
				const loadedField = loadedForm.form_fields?.find(
					(temp) => temp.id === rField.id
				);

				// Find the translated content for the field in the new language, if any
				const fieldTranslation = loadedField?.form_field_contents.find(
					(fContent) => fContent.language === newLanguage
				);

				// If a translation exists, add it to the form state

				if (fieldTranslation) {

					rField[FIELD_EDITOR_FIELDS.CONTENTS].push({
						language: newLanguage,
						[FIELD_EDITOR_FIELDS.TITLE]:
							fieldTranslation.title,
						[FIELD_EDITOR_FIELDS.PLACEHOLDER]:
							fieldTranslation.placeholder,
						[FIELD_EDITOR_FIELDS.HELP]:
							fieldTranslation.help,
						[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_CORRECT]:
							fieldTranslation.submission_feedback_correct,
						[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_INCORRECT]:
							fieldTranslation.submission_feedback_incorrect,
					});

				}

				// Loop through each option in the field and add the new language content

				rField[FIELD_EDITOR_FIELDS.OPTIONS].forEach((rOption) => {

					// Find the matching option in the loaded form data
					const loadedOption = loadedField?.form_field_options?.find(
						(temp) => temp.id === rOption.id
					);

					// Find the translated content for the option in the new language, if any
					const optionTranslation = loadedOption?.form_field_option_contents.find(
						(oContent) => oContent.language === newLanguage
					);

					// If a translation exists, add it to the form state

					if (optionTranslation) {

						rOption[OPTION_EDITOR_FIELDS.CONTENTS].push({
							language: newLanguage,
							[OPTION_EDITOR_FIELDS.LABEL]: optionTranslation.label,
						});

					}

				});

			});

		});

	}

	// Backfill any content that is missing a translation

	const translationExistsAfterLoaded = hasTranslationContent(result, newLanguage);

	if (translationExistsAfterLoaded && !translationDeleted) {

		// Loop through each section in the form and backfill missing language content

		result[FORM_EDITOR_FIELDS.SECTIONS].forEach((rSection) => {

			// Check if translation exists for the section
			const sectionTranslationIdx = rSection[SECTION_EDITOR_FIELDS.CONTENTS]
				.findIndex((content) => content.language === newLanguage);

			// Add a new empty translation to the section
			if (sectionTranslationIdx === -1) {

				rSection[SECTION_EDITOR_FIELDS.CONTENTS].push({
					language: newLanguage,
					[SECTION_EDITOR_FIELDS.TITLE]: null,
					[SECTION_EDITOR_FIELDS.DESCRIPTION]: null,
				});

			}

			// Loop through each field in the section and backfill missing language content

			rSection[SECTION_EDITOR_FIELDS.FIELDS].forEach((rField) => {

				// Check if translation exists for the field
				const fieldTranslationIdx = rField[FIELD_EDITOR_FIELDS.CONTENTS]
					.findIndex((content) => content.language === newLanguage);

				// Add a new empty translation to the field
				if (fieldTranslationIdx === -1) {

					rField[FIELD_EDITOR_FIELDS.CONTENTS].push({
						language: newLanguage,
						[FIELD_EDITOR_FIELDS.TITLE]: null,
						[FIELD_EDITOR_FIELDS.PLACEHOLDER]: null,
						[FIELD_EDITOR_FIELDS.HELP]: null,
						[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_CORRECT]: null,
						[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_INCORRECT]: null,
					});

				}

				// Loop through each option in the field and backfill missing language content

				rField[FIELD_EDITOR_FIELDS.OPTIONS].forEach((rOption) => {

					// Check if translation exists for the option
					const optionTranslationIdx = rOption[OPTION_EDITOR_FIELDS.CONTENTS]
						.findIndex((content) => content.language === newLanguage);

					// Add a new empty translation to the option
					if (optionTranslationIdx === -1) {

						// Check if the option label text and value should be in-sync

						const syncLabelAndValue =
							rField[FIELD_EDITOR_FIELDS.TYPE] === FIELD_TYPES.LIKERT;

						const optionLabel =
							syncLabelAndValue ? rOption[OPTION_EDITOR_FIELDS.VALUE].toString() : null;

						rOption[OPTION_EDITOR_FIELDS.CONTENTS].push({
							language: newLanguage,
							[OPTION_EDITOR_FIELDS.LABEL]: optionLabel,
						});

					}

				});

			});

		});

	}

	return {
		...result,
		[FORM_EDITOR_FIELDS.LANGUAGE]: fieldValue,
	};

}

/**
 * Adds a new multi-language content object to the form data state
 * for the currently selected language.
 *
 * @param {Object} formData  The current form data state.
 *
 * @returns {Object} result  The updated form data state.
 */
function addTranslation(formData) {

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Get the currently selected language
	const language = formData[FORM_EDITOR_FIELDS.LANGUAGE];

	// Check to see if a translation exists for the language
	const translationExists = hasTranslationContent(formData, language);

	if (!translationExists) {

		// Loop through each section in the form and add the new language content

		result[FORM_EDITOR_FIELDS.SECTIONS].forEach((rSection) => {

			// Add a new empty translation to the section

			rSection[SECTION_EDITOR_FIELDS.CONTENTS].push({
				language,
				[SECTION_EDITOR_FIELDS.TITLE]: null,
				[SECTION_EDITOR_FIELDS.DESCRIPTION]: null,
			});

			// Check if translation was set to be deleted for the section

			const deletedSectionTranslationIdx =
				rSection[SECTION_EDITOR_FIELDS.DELETED_CONTENTS]
					.findIndex((content) => content.language === language);

			// If so, make sure it is no longer marked for deletion

			if (deletedSectionTranslationIdx >= 0) {
				rSection[SECTION_EDITOR_FIELDS.DELETED_CONTENTS].splice(deletedSectionTranslationIdx, 1);
			}

			// Loop through each field in the section and add the new language content

			rSection[SECTION_EDITOR_FIELDS.FIELDS].forEach((rField) => {

				// Add a new empty translation to the field

				rField[FIELD_EDITOR_FIELDS.CONTENTS].push({
					language,
					[FIELD_EDITOR_FIELDS.TITLE]: null,
					[FIELD_EDITOR_FIELDS.PLACEHOLDER]: null,
					[FIELD_EDITOR_FIELDS.HELP]: null,
					[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_CORRECT]: null,
					[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_INCORRECT]: null,
				});

				// Check if translation was set to be deleted for the field

				const deletedFieldTranslationIdx =
					rField[FIELD_EDITOR_FIELDS.DELETED_CONTENTS]
						.findIndex((content) => content.language === language);

				// If so, make sure it is no longer marked for deletion

				if (deletedFieldTranslationIdx >= 0) {
					rField[FIELD_EDITOR_FIELDS.DELETED_CONTENTS].splice(deletedFieldTranslationIdx, 1);
				}

				// Loop through each option in the field and add the new language content

				rField[FIELD_EDITOR_FIELDS.OPTIONS].forEach((rOption) => {

					// Check if the option label text and value should be in-sync

					const syncLabelAndValue =
						rField[FIELD_EDITOR_FIELDS.TYPE] === FIELD_TYPES.LIKERT;

					const optionLabel =
						syncLabelAndValue ? rOption[OPTION_EDITOR_FIELDS.VALUE].toString() : null;

					// Add a new empty translation to the option

					rOption[OPTION_EDITOR_FIELDS.CONTENTS].push({
						language,
						[OPTION_EDITOR_FIELDS.LABEL]: optionLabel,
					});

					// Check if translation was set to be deleted for the option

					const deletedOptionTranslationIdx =
						rOption[OPTION_EDITOR_FIELDS.DELETED_CONTENTS]
							.findIndex((content) => content.language === language);

					// If so, make sure it is no longer marked for deletion

					if (deletedOptionTranslationIdx >= 0) {
						rOption[OPTION_EDITOR_FIELDS.DELETED_CONTENTS].splice(deletedOptionTranslationIdx, 1);
					}

				});

			});

		});

	}

	return result;

}

/**
 * Removes a multi-language content from the form data state
 * for the currently selected language and adds it to the list of
 * multi-language contents to delete.
 *
 * @param {Object} formData  The current form data state.
 *
 * @returns {Object} result  The updated form data state.
 */
function deleteTranslation(formData) {

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Get the currently selected language
	const language = formData[FORM_EDITOR_FIELDS.LANGUAGE];

	// Loop through each section in the form and remove the translated content

	result[FORM_EDITOR_FIELDS.SECTIONS].forEach((rSection) => {

		// Get the active translation for the section

		const activeSectionTranslationIdx =
			rSection[SECTION_EDITOR_FIELDS.CONTENTS]
				.findIndex((content) => content.language === language);

		const activeSectionTranslation =
			rSection[SECTION_EDITOR_FIELDS.CONTENTS][activeSectionTranslationIdx];

		// Remove active translation from current set of form section contents

		rSection[SECTION_EDITOR_FIELDS.CONTENTS].splice(activeSectionTranslationIdx, 1);

		// Add active translation to list of form section contents to delete

		if (formData.id) {
			rSection[SECTION_EDITOR_FIELDS.DELETED_CONTENTS].push(activeSectionTranslation);
		}

		// Loop through each field in the section and remove the translated content

		rSection[SECTION_EDITOR_FIELDS.FIELDS].forEach((rField) => {

			// Get the active translation for the field

			const activeFieldTranslationIdx =
				rField[FIELD_EDITOR_FIELDS.CONTENTS]
					.findIndex((content) => content.language === language);

			const activeFieldTranslation =
				rField[FIELD_EDITOR_FIELDS.CONTENTS][activeFieldTranslationIdx];

			// Remove active translation from current set of form field contents

			rField[FIELD_EDITOR_FIELDS.CONTENTS].splice(activeFieldTranslationIdx, 1);

			// Add active translation to list of form field contents to delete

			if (formData.id) {
				rField[FIELD_EDITOR_FIELDS.DELETED_CONTENTS].push(activeFieldTranslation);
			}

			// Loop through each option in the field and remove the translated content

			rField[FIELD_EDITOR_FIELDS.OPTIONS].forEach((rOption) => {

				// Get the active translation for the option

				const activeOptionTranslationIdx =
					rOption[OPTION_EDITOR_FIELDS.CONTENTS]
						.findIndex((content) => content.language === language);

				const activeOptionTranslation =
					rOption[OPTION_EDITOR_FIELDS.CONTENTS][activeOptionTranslationIdx];

				// Remove active translation from current set of form field option contents

				rOption[OPTION_EDITOR_FIELDS.CONTENTS].splice(activeOptionTranslationIdx, 1);

				// Add active translation to list of form field option contents to delete

				if (formData.id) {
					rOption[OPTION_EDITOR_FIELDS.DELETED_CONTENTS].push(activeOptionTranslation);
				}

			});

		});

	});

	return result;

}
